import { motion } from 'framer-motion';
import { Phone, CheckCircle, Smartphone, Shield, Clock, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import { FAQ } from '../components/FAQ';
import { Contact } from '../components/Contact';

const WHATSAPP_NUMBER = "5521980277322";

const services = [
  "Troca de tela iPhone e Samsung",
  "Substituição de bateria",
  "Reparo de conector de carga",
  "Troca de câmera frontal e traseira",
  "Reparo de alto-falante e microfone",
  "Recuperação de placa-mãe",
  "Atualização de software",
  "Remoção de conta iCloud/Google",
  "Backup e transferência de dados",
  "Troca de botões e sensores",
];

const brands = [
  { name: "Apple iPhone", models: "iPhone 8 ao iPhone 15 Pro Max" },
  { name: "Samsung Galaxy", models: "Galaxy S, A, M, Note, Z Fold/Flip" },
  { name: "Xiaomi", models: "Redmi, Mi, Poco, todas as linhas" },
  { name: "Motorola", models: "Moto G, E, Edge, Razr" },
];

const CelularPage = () => {
  return (
    <div data-testid="celular-page">
      {/* Hero Section */}
      <section className="hero-bg min-h-[60vh] flex items-center pt-32 md:pt-40 pb-16">
        <div className="hero-content max-w-7xl mx-auto px-6 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <Link to="/" className="text-[#00F0FF] text-sm font-mono uppercase tracking-widest hover:underline mb-4 inline-block">
              ← Voltar para Início
            </Link>
            
            <div className="inline-block mb-6">
              <span className="text-[#00F0FF] text-sm font-mono uppercase tracking-widest border border-[#00F0FF]/30 px-4 py-2 bg-[#00F0FF]/5">
                [ Assistência Técnica Celular ]
              </span>
            </div>
            
            <h1 className="font-orbitron text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Reparo de <span className="text-[#00F0FF] neon-text">Celular</span>
              <br />iPhone, Samsung, Xiaomi
            </h1>
            
            <p className="text-[#94A3B8] text-lg md:text-xl font-space leading-relaxed mb-8">
              Conserto especializado de smartphones com garantia. Troca de tela, bateria, 
              conector de carga e muito mais. Orçamento grátis e atendimento rápido na Barra da Tijuca.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <motion.a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=Olá! Preciso de assistência técnica para meu celular.`}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="whatsapp-btn justify-center text-base"
                data-testid="celular-whatsapp-btn"
              >
                <Phone size={20} />
                Orçamento pelo WhatsApp
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      <section className="section-darker py-20 grid-bg">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Services */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-orbitron text-2xl md:text-3xl font-bold text-white mb-8">
                Serviços de <span className="text-[#00F0FF]">Reparo</span>
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {services.map((service, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle size={18} className="text-[#00F0FF] flex-shrink-0" />
                    <span className="text-sm text-[#94A3B8] font-space">{service}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Brands */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-orbitron text-2xl md:text-3xl font-bold text-white mb-8">
                Marcas <span className="text-[#00F0FF]">Atendidas</span>
              </h2>
              <div className="space-y-4">
                {brands.map((brand, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="cyber-card p-6"
                  >
                    <div className="flex items-center gap-4">
                      <Smartphone size={24} className="text-[#00F0FF]" />
                      <div>
                        <h3 className="font-rajdhani font-bold text-white text-lg">{brand.name}</h3>
                        <p className="text-sm text-[#94A3B8]">{brand.models}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-dark py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-orbitron text-2xl md:text-3xl font-bold text-white text-center mb-12">
            Por que escolher a <span className="text-[#00F0FF]">Vaz Tech</span>?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Shield, title: "Garantia de 90 dias", desc: "Todos os reparos com garantia total em peças e serviços." },
              { icon: Clock, title: "Reparo Rápido", desc: "Maioria dos reparos em até 2 horas. Atendimento urgente disponível." },
              { icon: Award, title: "Técnicos Certificados", desc: "Equipe especializada com anos de experiência no mercado." },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="cyber-card p-8 text-center"
              >
                <div className="w-16 h-16 mx-auto mb-6 border border-[#00F0FF]/30 flex items-center justify-center bg-[#00F0FF]/5">
                  <item.icon size={28} className="text-[#00F0FF]" />
                </div>
                <h3 className="font-rajdhani font-bold text-white text-xl mb-3">{item.title}</h3>
                <p className="text-sm text-[#94A3B8] font-space">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <FAQ />
      <Contact />
    </div>
  );
};

export default CelularPage;
